<template>
	<div v-if="editing">
		<div class="input-group mb-3">
			<input type="text" class="form-control" placeholder="ID" aria-label="ID" ref="fieldid" aria-describedby="basic-addon2" :disabled="fieldIdIsLocked" v-model="editing.id">
			<div class="input-group-append">
				<button class="btn btn-outline-secondary" type="button" @click="toggleFieldIdLocked">
					<template v-if="fieldIdIsLocked">{{ $t("edit") }}</template>
					<template v-else>{{ $t("lock") }}</template>
				</button>
			</div>
		</div>

        <div class="form-group">
			<label>{{ $t('type') }}</label>
			<select class="form-control" v-model="editing.type" @change="typeChange">
				<option v-for="(option, okey) in options" :key="okey" :value="option.value">
					{{ option.text }}
				</option>
			</select>
		</div>

		 <div class="form-group"  v-if="editing.type=='select'">
			<div class="btn-group block">

				<a
				class="btn"
				:class="{
					'btn-light': editing.selectType != 'buttons' && editing.selectType /* backwards compatibity - default to buttons */,
					'btn-success': editing.selectType == 'buttons' || !editing.selectType /* backwards compatibity - default to buttons */
				}"
				@click="editing.selectType='buttons'"
				>{{$t('buttons')}}</a>

				<a
				class="btn"
				:class="{
					'btn-light': editing.selectType != 'dropdown',
					'btn-success': editing.selectType == 'dropdown'
				}"
				@click="editing.selectType='dropdown'"
				>{{$t("dropdown")}}</a>

			</div>
		</div>

		<template v-if="!['note_plaintext', 'note_richtext', 'note_html', 'heading'].includes(editing.type)">
			<div class="form-group">
				<label>{{ $t('question') }}</label>
				<input class="form-control" v-model="editing.question" :placeholder="$t('question')" @blur="tryGenerateFieldId(editing)">
			</div>
			<div class="form-group">
				<label>{{ $t("description") }}</label>
				<vue-editor v-model="editing.description"></vue-editor>
			</div>
		</template>

		<div class="form-group" v-if="editing.type=='select' || editing.type=='multiselect'">

			<label class="mr-1">{{ $t('options') }}</label>
			<a class="btn btn-light mr-1" @click="saveOptionsToLibrary(editing.options)" v-if="!editing.optionsFromConfig">{{ $t('save') }}</a>
			<a class="btn btn-light" @click="loadOptionsFromLibrary">{{ $t('load') }}</a>

			<div class="mt-2">
				<template v-if="editing.optionsFromConfig">
					{{ $t('use') }} <em>{{ editing.optionsFromConfig }}</em> {{ $t('config') }}. - <a href="#" @click.prevent="editing.optionsFromConfig = null">{{ $t("unlink") }}</a>
				</template>
				<template v-else>
					<OptionsEditor :options="editing.options"></OptionsEditor>
				</template>
			</div>
		</div>

		<div class="form-group" v-if="editing.type=='polararea'">

			<label class="mr-1">{{ $t('items') }}</label>

			<div class="mt-2">
				<template v-if="editing.optionsFromConfig">
					{{ $t('use') }} <em>{{ editing.optionsFromConfig }}</em> {{ $t('config') }}. - <a href="#" @click.prevent="editing.optionsFromConfig = null">{{ $t("unlink") }}</a>
				</template>
				<template v-else>
					<DataPointEditor :datapoints="editing.data"></DataPointEditor>
				</template>
			</div>
		</div>

		<div class="form-group" v-if="editing.type=='bool'">
			<label>{{ $t('on-style') }}</label>
			<BtnOnClassPicker :option="editing"></BtnOnClassPicker>
		</div>

		<div class="form-group" v-if="editing.type=='note_plaintext'">
			<label>{{ $t('text') }}</label>
			<textarea class="form-control" v-model="editing.text"></textarea>
		</div>

		<div class="form-group" v-if="editing.type=='note_richtext'">
			<label>{{ $t('text') }}</label>
			<vue-editor v-model="editing.text"></vue-editor>
		</div>

		<div class="form-group" v-if="editing.type=='note_html'">
			<label>{{ $t('text') }}</label>
			<textarea class="form-control" v-model="editing.text"></textarea>
		</div>

		<div class="form-group" v-if="editing.type=='heading'">
			<label>{{ $t('text') }}</label>
			<input class="form-control" v-model="editing.text">
		</div>

		<div class="form-group" v-if="Object.hasOwnProperty.call(editing, 'maxWords')">
			<label>{{ $t('max-words') }}</label>
			<input type="number" class="form-control" v-model="editing.maxWords" min="0">
		</div>

		<div class="form-group" v-if="editing.type=='select'||editing.type=='text'||editing.type=='textarea'||editing.type=='number'||editing.type=='computed'">
			<label>{{ $t('label-style') }}</label>
			<div :value="editing.labelStyle" class="btn-group block">
				<a v-for="(labelStyle, lkey) in labelStyles" :key="lkey" :class="{ 'btn': true , 'btn-light': editing.labelStyle != labelStyle.value , 'btn-success': editing.labelStyle == labelStyle.value }"  @click="editing.labelStyle=labelStyle.value" :value="labelStyle.value">{{ labelStyle.text }}</a>
			</div>
		</div>

		<template v-if="editing.type=='computed' && ('computesettings' in editing)">
			<div class="form-group">
				<label>{{ $t('get-values-from') }}</label>
				<select class="form-control" v-model="editing.computesettings.valuesfrom" @change="typeChange">
					<option value="all">{{ $t('whole-form') }}</option>
					<option value="page">{{ $t('selected-pages') }}</option>
					<option value="fields">{{ $t('selected-fields') }}</option>
				</select>
			</div>
			<div v-if="editing.computesettings.valuesfrom == 'page'" class="form-group">
				<label>{{ $t('pages') }}</label>
				<div>
					<span
						class="btn btn-sm mr-1"
						:class="{
							'btn-success': editing.computesettings.pages.includes(index) ,
							'btn-light': !editing.computesettings.pages.includes(index)
						}"
						v-for="(page, index) in form.pages"
						:key="index"
						@click="togglePageSelect(index)"
					>
						<b-icon v-if="editing.computesettings.pages.includes(index)" icon="check-square"></b-icon>
						<b-icon v-else icon="square"></b-icon>
						<template v-if="page.title">
							{{page.title}}
						</template>
						<template v-else>
							{{ index + 1 }}
						</template>
					</span>
				</div>
			</div>
			<div v-if="editing.computesettings.valuesfrom == 'fields'" class="form-group">
				<label>{{ $t('pages') }}</label>
				<div>
					<template v-for="page in form.pages">
						<template v-for="row in page.grid">
							<template v-for="col in row">
								<template v-for="field in col.fields">
									<span
										v-if="field.type == 'number' | field.type == 'select' | field.type == 'text' | field.type == 'textarea'"
										:key="field.id"
										class="btn btn-sm mr-1"
										:class="{
											'btn-success': editing.computesettings.fields.includes(field.id) ,
											'btn-light': !editing.computesettings.fields.includes(field.id)
										}"
										@click="toggleFieldSelect(field.id)"
									>
										<b-icon v-if="editing.computesettings.fields.includes(field.id)" icon="check-square"></b-icon>
										<b-icon v-else icon="square"></b-icon>
										{{field.question}}
									</span>
								</template>
							</template>
						</template>
					</template>
				</div>
			</div>
		</template>

		<div class="form-group" v-if="'permission' in editing">
			<label>
				<template v-if="isInputField(editing)">
					{{ $t('who-can-complete-field') }}
				</template>
				<template v-else>
					{{ $t('who-can-see-field') }}
				</template>
			</label>
			<div :value="editing.permission" class="btn-group block">
				<a :class="{ 'btn': true , 'btn-light': editing.permission.length>0 , 'btn-success': editing.permission.length===0 }" v-on:click="editing.permission=[]">{{ $t('all') }}</a>
				<a v-for="(permission, pkey) in permissions" :key="pkey" :class="{ 'btn': true , 'btn-light': !editing.permission.includes(permission.value) , 'btn-success': editing.permission.includes(permission.value) }" v-on:click="editing.permission=togglePermission(permission.value)" :value="permission.value">{{ permission.text }}</a>
			</div>
		</div>
		<template v-if="!'permission' in editing || editing.permission.length">
			<div class="form-check">
				<label class="form-check-label" for="hideifcantcomplete">
					<input type="checkbox" class="form-check-input" id="hideifcantcomplete" v-model="editing.hideifcantcomplete">
					{{ $t('hide-from-other-users') }}
				</label>
			</div>
			<div class="form-group" v-if="editing.hideifcantcomplete">
				<div class="btn-group block">
					<a class="btn" :class="{'btn-light':editing.showaftersignoff || editing.showafterreview,'btn-success':!(editing.showaftersignoff && editing.showafterreview) }" @click="resetFields" >{{$t('always')}}</a>
					<a class="btn" :class="{'btn-light':!editing.showafterreview,'btn-success':editing.showafterreview}" @click='clickUntilRev'>{{$t('until-reviewed')}}</a><!--before we had editing.showafterreview=true using editing.showafterreview!=editing.showafterreview-->
					<a class="btn" :class="{'btn-light':!editing.showaftersignoff,'btn-success':editing.showaftersignoff}" @click="editing.showaftersignoff=!editing.showaftersignoff" >{{$t('until-signed-off')}}</a>
				</div>
			</div>
		</template>
	</div>
</template>

<script>
import { VueEditor } from "vue2-editor";
import OptionsEditor from "./OptionsEditor";
import DataPointEditor from "./DataPointEditor";
import BtnOnClassPicker from "./BtnOnClassPicker";
export default {
	components: {
		VueEditor,
		OptionsEditor,
		DataPointEditor,
		BtnOnClassPicker
	},
	name: 'FieldEditor',
	props: ['editing', 'form', 'permissions'],
	data() {
		return {
			userWantsToEditFieldId: false,
			options: [
				{
					value: 'bool' ,
					text: this.$t('boolean')
				},
				{
					value: 'text' ,
					text: this.$t('text-single-line')
				},
				{
					value: 'textarea' ,
					text: this.$t('text-multiline')
				},
				{
					value: 'richtext',
					text: this.$t('rich-text')
				},
				{
					value: 'number' ,
					text: this.$t('number')
				},
				// todo: add option to allow select multiple
				{
					value: 'select' ,
					text: this.$t('select')
				},
				{
					value: 'multiselect' ,
					text: this.$t('multiselect')
				},
				{
					value: 'fileupload',
					text: this.$t('file-upload')
				},
				{
					value: 'signature',
					text: this.$t('signature')
				},
				{
					value: 'datetime',
					text: this.$t('date-time')
				},
				{
					value: 'date',
					text: this.$t('date')
				},
				{
					value: 'polararea' ,
					text: this.$t('polar-area')
				},
				{
					value: 'heading' ,
					text: this.$t('heading')
				},
				{
					value: 'note_plaintext' ,
					text: this.$t('note-plaintext')
				},
				{
					value: 'note_richtext' ,
					text: this.$t('note-richtext')
				},
				{
					value: 'note_html' ,
					text: this.$t('note-html')
				},
				{
					value: 'computed' ,
					text: this.$t('computed-value')
				}
			],
			labelStyles: [
				{
					value: 'block',
					text: this.$t('default')
				},
				{
					value: 'inline',
					text: this.$t('inline')
				},
				{
					value: 'justify',
					text: this.$t('justify')
				}
			],
		}
	},
	methods: {
		togglePermission(permission) {
			const updatedPermission = [...this.editing.permission];
			const index = this.editing.permission.indexOf(permission);
			if(permission){
				if (index === -1) {
					// permission not found in the array, add it
					updatedPermission.push(permission);
				} else {
					// permission found in the array, remove it
					updatedPermission.splice(index, 1);
				}
				this.editing.permission = updatedPermission;
				if (this.editing.permission.length==this.permissions.length){
					this.editing.permission=[];
				}
			} else {
				this.editing.permission=[]
			}
			return this.editing.permission;

  		},
		resetFields(){
			this.editing.showaftersignoff=false;
			this.editing.showafterreview=false;
		},
		clickUntilRev(){
			this.editing.showafterreview=!this.editing.showafterreview;
			this.editing.showaftersignoff=true;
		},
		loadOptionsFromLibrary() {

        	const data = {
        		type: 'selectoptions'
			}

 			this.$cfetch(`${this.$settings.APIBaseURL}/formlibrary/list`, {
				data
			})
			.then(json => {
				if (json.status === "SUCCESS") {

					const items = json.items;

					// add hardwired config items (specialties, clinics etc)
					items.push({
						'formlibrary_type': 'selectoptionsfromconfig',
						'formlibrary_title': 'Clinics (from config)',
						'config_name': 'clinics'
					});
					items.push({
						'formlibrary_type': 'selectoptionsfromconfig',
						'formlibrary_title': 'Specialties (from config)',
						'config_name': 'specialties'
					});

					const self = this;
					this.$store.commit("openModal", {
						type: "selector",
						title: this.$t('select-options'),
						selectCallback: function(selection) {
							if(selection.formlibrary_type == 'selectoptions') {
								self.$set(self.editing, 'options', selection.formlibrary_json);
								self.$set(self.editing, 'optionsFromConfig', null);
							} else if(selection.formlibrary_type == 'selectoptionsfromconfig') {
								self.$set(self.editing, 'options', []);
								self.$set(self.editing, 'optionsFromConfig', selection.config_name);
							}
							self.$store.commit("closeModal");
						},
						selected: [],
						data: items,
						captionKey: 'formlibrary_title',
						subcaptionKey: 'formlibrary_notes'
					});
				}
			})
			.catch(error => {
				this.$log.error.call(this, error);
			})
		},
    	getDefaultField(type = 'text') {
    		let field;
    		if(this.isInputField({type})) {
    			field = {
					question: null,
					description: null,
					type,
					value: null,
					permission: [],
					hideifcantcomplete: false,
					showaftersignoff: false,
					showafterreview: false,
					labelStyle: 'block',
					id: null
				}

				if(type == 'select') {
					// add extra properties for select fields
					field = Object.assign(field, {
						selectType: 'buttons',
						optionsFromConfig: null,
						options: []
					})
				} else if(type == 'multiselect') {
					// add extra properties for select fields
					field = Object.assign(field, {
						selectType: 'buttons',
						optionsFromConfig: null,
						options: [] ,
						value: []
					});
				} else if(['textarea'/*, 'richtext'*/].includes(type)) {
					// add max words property
					field.maxWords = null;
				} else if(type == 'polararea') {
					// add extra properties for select fields
					field = Object.assign(field, {
						data: []
					})
				}


    		} else {
    			// headings, notes
    			field = {
    				type,
    				text: null,
					permission: [],
					hideifcantcomplete: false,
					showaftersignoff: false,
					showafterreview: false,
    			}
    			if(type == 'computed') {
					// add extra properties for select fields
					field = Object.assign(field, {
						question: null,
						description: null,
						labelStyle: 'block',
						computesettings: {
							valuesfrom: null ,
							pages: [] ,
							fields: [] ,
							textmapping: {}
						}
					});
				}
    		}

			return field;
    	},
    	isInputField(field) {
    		return ['text', 'textarea', 'number', 'select', 'multiselect', 'bool', 'fileupload', 'signature', 'richtext', 'datetime', 'date', 'polararea'].includes(field.type);
    	},
    	toggleFieldIdLocked() {
    		this.userWantsToEditFieldId = !this.userWantsToEditFieldId;
    	},
    	typeChange(e) {
    		// catch edge case where changing type from fileupload to something else
    		// fileupload val is an array which will cause errors with other fields that expect a string
    		if(e.target.value == 'multiselect'){
    			this.editing.value = [];
    		} else if(e.target.value != 'fileupload' && Array.isArray(this.editing.value)) {
    			this.editing.value = null;
    		}
    		this.patchField(this.editing);
    		if(!this.isInputField(this.editing)) {
    			this.editing.hideifcantcomplete = true;
    		}
    	},
		patchField(field) {
			const defaultField = this.getDefaultField(field.type);
			for (const [key, value] of Object.entries(defaultField)) {
				if(!Object.hasOwnProperty.call(field, key)) {
					this.$set(field, key, value);
				}
			}
			// remove properties not in default field
			for (const key of Object.keys(field)) {
				if(!Object.hasOwnProperty.call(defaultField, key)) {
					this.$delete(field, key);
				}
			}
			//this.tryGenerateFieldId(field);
		},
    	tryGenerateFieldId(field) {
    		if(Object.hasOwnProperty.call(field, 'id') && !field.id && field.question) {
    			// trim question
    			const question = field.question.trim();
	    		// replace all except word chars and whitespace
	    		let id = question.replace(/[^\w\s]/g,'');
	    		// replace any whitepace with a hyphen
	    		id = id.replace(/\s+/g, '-');
	    		// convert to lowercase
	    		id = id.toLowerCase();
	    		field.id = id;
	    	}
    	},
    	setUserWantsToEditField(val) {
    		this.userWantsToEditFieldId = val;
    	},
    	saveItemToLibrary(item, type, dialogTitle, firstDialogPrompt) {
			const self = this;
			this.$store.commit("openDialog" , {
				title: dialogTitle,
				prompt: firstDialogPrompt,
				confirmBtnText: this.$t('continue'),
				cancelBtn: true,
				confirmCallBack: function(title) {

					self.$nextTick(function() {
						self.$store.commit("openDialog" , {
							title: dialogTitle,
							prompt: this.$t('notes'),
							multilinePrompt: true,
							confirmBtnText: this.$t('save'),
							cancelBtn: true,
							confirmCallBack: function(notes) {

					        	const data = {
									settings: item,
									title,
									type, // tab, row, field, fieldgroup, selectoptions
									notes
								}

					 			self.$cfetch(`${self.$settings.APIBaseURL}/formlibrary/save`, {
									method: "post",
									data
								})
								.then(json => {
									if (json.status === "SUCCESS") {
										self.$store.commit("openDialog" , {
											title: self.$t('saved-to-library'),
											content: self.$t('item-successfully-saved-to-library')
										});
									}
								})
								.catch(error => {
									self.$log.error.call(self, error);
								});
							}
						});
					})

				}
			});
    	},
    	saveFieldToLibrary(field) {

    		this.saveItemToLibrary(
    			field,
    			'field',
    			this.$t('save-field-to-library'),
    			this.$t('field-name')
    		);

    	},
    	saveOptionsToLibrary(options) {

    		this.saveItemToLibrary(
    			options,
    			'selectoptions',
    			this.$t('save-options-to-library'),
    			this.$t('options-name')
    		);

    	},
    	togglePageSelect(index){
    		var item = this.editing.computesettings.pages.indexOf(index);
    		if(item != -1){
    			this.editing.computesettings.pages.splice(item , 1);
    		} else {
    			this.editing.computesettings.pages.push(index)
    		}
    	},
    	toggleFieldSelect(id){
    		var item = this.editing.computesettings.fields.indexOf(id);
    		if(item != -1){
    			this.editing.computesettings.fields.splice(item , 1);
    		} else {
    			this.editing.computesettings.fields.push(id)
    		}
    	},
    	editIdField(){
    		this.userWantsToEditFieldId = true;
    		this.$refs.fieldid.focus();
    	}
	},
	computed: {
    	fieldIdIsLocked() {
    		return this.userWantsToEditFieldId ? null : true;
    	}
	}
};
</script>
